import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Observable, Subscription, take } from "rxjs";
import { DbClient, Invoice, Utils, DocumentLike, DocumentHelper } from 'fakturnia-shared';
import { ConfirmDialogComponent, ConfirmDialogData } from "../dialogs/confirm-dialog/confirm-dialog.component";
import { SearchGusCompanyDialogComponent } from "../dialogs/search-gus-company-dialog/search-gus-company-dialog.component";
import { SetUpCompanyDialogComponent } from "../dialogs/set-up-company-dialog/set-up-company-dialog.component";
import { AddOrUpdateBankAccountDialogComponent } from "../dialogs/add-or-update-bank-account-dialog/add-or-update-bank-account-dialog.component";
import { ToggleFormFieldsDialogComponent } from "../dialogs/toggle-form-fields-dialog/toggle-form-fields-dialog.component";
import { CreateOrUpdateClientDialogComponent } from "../dialogs/create-or-update-client-dialog/create-or-update-client-dialog.component";
import { SelectInvoiceDialogComponent } from "../dialogs/select-invoice-dialog/select-invoice-dialog.component";
import { ApiService } from "./api.service";
import { SynchronizationService } from "./synchronization.service";
import { SnackbarService } from "./snackbar.service";
import { APIResponse } from "fakturnia-shared";
import { IBankAccount } from "fakturnia-shared";
import { SendInvoiceByEmailDialogComponent } from "../dialogs/send-invoice-by-email-dialog/send-invoice-by-email-dialog.component";
import { DisplayFormErrorsDialogComponent } from "../dialogs/display-form-errors-dialog/display-form-errors-dialog.component";
import { DocumentsApiService } from "./api/documents.api.service";
import { AddClientDialogComponent } from "shared/dialogs/add-client-dialog/add-client-dialog.component";
import { DocumentLimitExceededDialogComponent } from "shared/dialogs/document-limit-exceeded-dialog/document-limit-exceeded-dialog.component";
import { UpgradeSubscriptionDialogComponent, UpgradeSubscriptionDialogData } from "shared/dialogs/upgrade-subscription-dialog/upgrade-subscription-dialog.component";
import { WelcomeOnboardingDialogComponent } from "shared/dialogs/welcome-onboarding-dialog/welcome-onboarding-dialog.component";

@Injectable({
  providedIn: 'root'
})
export class DialogService implements OnDestroy {
  private _subscriptions: Subscription[] = [];
  constructor(
    private dialog: MatDialog,
    private _apiService:ApiService,
    private _synchronizationService:SynchronizationService,
    private _snackbarService:SnackbarService,
    private _documentsApiService:DocumentsApiService
  ) {

  }

  confirmDialog(data: ConfirmDialogData): Observable<boolean | null> {
    let toReturn = new BehaviorSubject<boolean | null>(null);

    this._subscriptions.push(this.dialog.open(ConfirmDialogComponent, {
      data,
      panelClass: 'fakturnia-responsive-confirmation-dialog'
    })
      .afterClosed()
      .subscribe(
        {
          next: (data) => {

            if (!Utils.isDefined(data, "action")) { toReturn.next(false); return; }

            if (data.action == true) {
              toReturn.next(true)
            }
            else if (data.action == false) {
              toReturn.next(false)
            }
          },
          error: (err) => {
            toReturn.next(false)
          }
        }
      ))

    return toReturn.asObservable();
  }

  /**
   * Dialog potwierdzenia z formularzem - zwraca dane formularza
   */
  confirmDialogWithForm(data: ConfirmDialogData): Observable<{ confirmed: boolean; formValue?: any }> {
    let toReturn = new BehaviorSubject<{ confirmed: boolean; formValue?: any }>({ confirmed: false });

    this._subscriptions.push(this.dialog.open(ConfirmDialogComponent, {
      data,
      panelClass: 'fakturnia-responsive-dialog'
    })
      .afterClosed()
      .subscribe(
        {
          next: (result) => {
            if (!Utils.isDefined(result, "action")) {
              toReturn.next({ confirmed: false });
              return;
            }

            if (result.action === true) {
              toReturn.next({
                confirmed: true,
                formValue: result.form?.value || {}
              });
            } else {
              toReturn.next({ confirmed: false });
            }
          },
          error: () => {
            toReturn.next({ confirmed: false });
          }
        }
      ))

    return toReturn.asObservable();
  }

  confirmMarkAsPaidOrTemplateDialog(isPaid, selectedDocs, title:string | null = null, message:string | null = null) {
    let toReturn = new BehaviorSubject<boolean | null>(null);

    

    this.confirmDialog({
      title: title ? title : "Oznaczanie dokumentów",
      message:  message ? message : `Chcesz oznaczyć wybrane dokumenty? Ilość wybranych dokumentów: ${selectedDocs.length}`,
      type: "info",
      confirmText: "Oznacz",
      cancelText: "Anuluj"
    })
    .subscribe({
      next: (data) => {

        if(data != true) return

        this._documentsApiService.markDocumentsAsPaidOrTemplate(selectedDocs, isPaid)
        .pipe(take(1))
        .subscribe({
          next: (response:APIResponse) => {

            if(!response.success) {
              this._snackbarService.showSnackBar('Operacja się nie powiodła.','error')
              toReturn.next(false)
              return
            }

            this._synchronizationService.synchronize()
            this._snackbarService.showSnackBar("Operacja przebiegła pomyślnie.","success")
            toReturn.next(true)
          }

        })
      }
    })

    return toReturn.asObservable()
  }

  /**
   * Dialog potwierdzenia usunięcia dokumentu.
   * Używa DocumentHelper.getDocumentName() do wyświetlenia nazwy dokumentu.
   * @param document Dokument do usunięcia (Invoice, DbDocument lub DocumentLike)
   */
  confirmDeleteInvoiceDialog(document: Invoice | DocumentLike) {

    let toReturn = new BehaviorSubject<boolean | null>(null);
    const documentId = DocumentHelper.getDocumentId(document as DocumentLike);
    const documentName = DocumentHelper.getDocumentName(document as DocumentLike);

    if (!documentId) {
      console.error('confirmDeleteInvoiceDialog: brak ID dokumentu');
      toReturn.next(false);
      return toReturn.asObservable();
    }

    this.confirmDialog({
      title: "Usuwanie dokumentu",
      message: `Czy na pewno chcesz usunąć dokument ${documentName}?`,
      type: "danger",
      confirmText: "Usuń",
      cancelText: "Anuluj"
    })
    .subscribe({
        next: (data) => {

          if(data != true) return

          this._documentsApiService.delete(documentId)
          .pipe(take(1))
          .subscribe({
            next: (response:APIResponse) => {

              if(!response.success) {
                this._snackbarService.showSnackBar('Nie udało się usunąć tego dokumentu.','error')
                toReturn.next(false)
                return
              }

              this._synchronizationService.synchronize()
              this._snackbarService.showSnackBar('Dokument został usunięty','success')

              toReturn.next(true)

            }
          })
        }
      }
    )

    return toReturn.asObservable()
  }

  confirmDeleteInvoicesDialog(invoices, title:string | null = null, message:string | null = null) {

    let toReturn = new BehaviorSubject<boolean | null>(null);

    this.confirmDialog({
      title: title ? title : "Usuwanie dokumentów",
      message:  message ? message : `Chcesz usunąć zaznaczone elementy? Ilość usuwanych dokumentów: ${invoices.length}`,
      type: "danger",
      confirmText: "Usuń",
      cancelText: "Anuluj"
    })
    .subscribe({
        next: (data) => {

          if(data != true) return

          this._apiService.deleteDocuments(invoices)
          .pipe(take(1))
          .subscribe({
            next: (response:APIResponse) => {

              if(!response.success) {
                this._snackbarService.showSnackBar('Operacja usuwania się nie powiodła.','error')
                toReturn.next(false)
                return
              }

              this._synchronizationService.synchronize()
              this._snackbarService.showSnackBar('Operacja usuwania się powiodła.','success')
              toReturn.next(true)

            }
          })
        }
      }
    )

    return toReturn.asObservable()
  }

  public showSendInvoiceByEmailDialog(invoice:Invoice){

    this.dialog.open(SendInvoiceByEmailDialogComponent, {
      data: { invoice: invoice},
      width: '600px',
      panelClass: 'fakturnia-responsive-dialog'
    })

  }

  public showFormErrorsDialog(errors:{field: string, errors: string[]}[]){

    this.dialog.open(DisplayFormErrorsDialogComponent, {
      data: { errors: errors},
      panelClass: 'fakturnia-responsive-dialog',
      width: '500px'
    })

  }

  /**
   * Wyświetla dialog informujący o przekroczeniu limitu dokumentów w abonamencie
   */
  public showDocumentLimitExceededDialog(): void {
    this.dialog.open(DocumentLimitExceededDialogComponent, {
      panelClass: ['fakturnia-responsive-dialog', 'document-limit-exceeded-dialog'],
      width: '480px',
      maxWidth: '95vw',
      disableClose: false
    });
  }

  /**
   * Wyświetla dialog zachęcający do upgrade'u subskrypcji
   * @param data - dane dialogu (wymagany plan, nazwa funkcji, ikona)
   */
  public showUpgradeSubscriptionDialog(data: UpgradeSubscriptionDialogData): void {
    this.dialog.open(UpgradeSubscriptionDialogComponent, {
      data,
      panelClass: ['fakturnia-responsive-dialog', 'upgrade-subscription-dialog'],
      width: '480px',
      maxWidth: '95vw',
      disableClose: false
    });
  }

    public showSelectInvoiceDialog({filters, selection = 'multiple', selectedInvoicesIds = []}: { filters:Function[], selection: 'single' | 'multiple', selectedInvoicesIds?: string[]}): Observable<boolean | null | any> {
    let toReturn = new BehaviorSubject<boolean | null>(null);

    this._subscriptions.push(this.dialog.open(SelectInvoiceDialogComponent, {
      data: { filters: filters, selection: selection, selectedInvoicesIds: selectedInvoicesIds},
      panelClass: 'fakturnia-responsive-dialog',
      width: '900px'
    })
      .afterClosed()
      .subscribe(
        {
          next: (data) => {
            if(!data) { 
              toReturn.next(false)
              return
            }
            else if(data.invoices)  toReturn.next(data.invoices)

          },
          error: (err) => {
            toReturn.next(false)
          }
        }
      ))

    return toReturn.asObservable();
  }


  showAddClientDialog(): Observable<boolean | null> {
    let toReturn = new BehaviorSubject<any>({
      success: null,
      data: null
    });

    this._subscriptions.push(this.dialog.open(AddClientDialogComponent, {
      panelClass: 'fakturnia-responsive-dialog'
    })
      .afterClosed()
      .subscribe(
        {
          next: (response) => {

            if(!response || typeof response.success == 'undefined') {
              toReturn.next({
                success: false,
                data: null
              })
              return
            }

            if(response.success) {
              toReturn.next({
                success: true,
                data: response.data
              })
              return
            }

            toReturn.next({
              success: false,
              data: null
            })
          },
          error: (err) => {
            toReturn.next({
              success: false,
              data: null
            })
          }
        }
      ))

    return toReturn.asObservable();
  }

  showAddOrUpdateBankAccount(bankAccount:IBankAccount | undefined): Observable<boolean | null> {
    let toReturn = new BehaviorSubject<any>({
      success: null,
      data: null
    });

    this._subscriptions.push(this.dialog.open(AddOrUpdateBankAccountDialogComponent, {
      width: '512px',
      panelClass: 'fakturnia-responsive-dialog',
      data: bankAccount
    })
      .afterClosed()
      .subscribe(
        {
          next: (response) => {

            if(!response || typeof response.success == 'undefined') {
              toReturn.next({
                success: false,
                data: null
              })
              return
            }

            if(response.success) {
              toReturn.next({
                success: true,
                data: response.data
              })
              return
            }

            toReturn.next({
              success: false,
              data: null
            })
          },
          error: (err) => {
            toReturn.next({
              success: false,
              data: null
            })
          }
        }
      ))

    return toReturn.asObservable();
  }

  showSetUpCompanyDialog(): Observable<boolean | null> {
    let toReturn = new BehaviorSubject<any>({
      success: null,
      data: null
    });

    this._subscriptions.push(this.dialog.open(SetUpCompanyDialogComponent, {
      width: '640px',
      maxWidth: '90vw',
      disableClose: true,
      panelClass: ['fakturnia-responsive-dialog', 'setup-company-dialog']
    })
      .afterClosed()
      .subscribe(
        {
          next: (response) => {

            if(!response || typeof response.success == 'undefined') {
              toReturn.next({
                success: false,
                data: null
              })
              return
            }

            if(response.success) {
              toReturn.next({
                success: true,
                data: response.data
              })
              return
            }

            toReturn.next({
              success: false,
              data: null
            })
          },
          error: (err) => {
            toReturn.next({
              success: false,
              data: null
            })
          }
        }
      ))

    return toReturn.asObservable();
  }

  showSearchGUSResultsDialog(data: any): Observable<boolean | null> {
    let toReturn = new BehaviorSubject<any>({
      action: null,
      company: null
    });

    this._subscriptions.push(this.dialog.open(SearchGusCompanyDialogComponent, {
      data,
      width: '512px'
    })
      .afterClosed()
      .subscribe(
        {
          next: (data) => {

            if (!Utils.isDefined(data, "action")) {
              toReturn.next({
                action: false,
                company: null
              })
              return
            }

            if (data.action == true) {
              toReturn.next({
                action: true,
                company: data.company
              })
            }
            else if (data.action == false) {
              toReturn.next({
                action: false,
                company: null
              })
            }
          },
          error: (err) => {
            toReturn.next({
              action: false,
              company: null
            })
          }
        }
      ))

    return toReturn.asObservable();
  }

  showToggleFormFieldsDialog({
    currency,
    generalFieldsToShow,
    documentType
  }): Observable<any> {
    let toReturn = new BehaviorSubject<any>({
      success: null,
      data: null
    });

    this._subscriptions.push(this.dialog.open(ToggleFormFieldsDialogComponent, {
      width: '650px',
      panelClass: 'fakturnia-responsive-dialog',
      data: {
        currency: currency,
        generalFieldsToShow: generalFieldsToShow,
        documentType: documentType
      }
    })
      .afterClosed()
      .subscribe(
        {
          next: (response) => {

            if(!response || typeof response.success == 'undefined') {
              toReturn.next({
                success: false,
                data: null
              })
              return
            }

            if(response.success) {
              toReturn.next({
                success: true,
                data: response.data
              })
              return
            }

            toReturn.next({
              success: false,
              data: null
            })
          },
          error: (err) => {
            toReturn.next({
              success: false,
              data: null
            })
          }
        }
      ))

    return toReturn.asObservable();
  }

  showCreateOrUpdateClientDialog(client: DbClient, clientType?: 'seller' | 'buyer' | 'receiver',): Observable<any> {
    let toReturn = new BehaviorSubject<any>({
      success: null,
      client: null
    });

    this._subscriptions.push(this.dialog.open(CreateOrUpdateClientDialogComponent, {
      // width: '100%',
      maxWidth: '100%',
      panelClass: 'fakturnia-responsive-dialog',
      data: {
        client: client,
        clientType: clientType,
      }
    })
      .afterClosed()
      .subscribe({
          next: (response) => {

            if(!response || typeof response === 'undefined' || typeof response.success == 'undefined') {
              toReturn.next({
                success: false,
                client: null
              })
              return
            }

            if(response.success) {
              toReturn.next(response)
              return
            }

            toReturn.next({
              success: false,
              client: null
            })
          },
          error: (err) => {
            toReturn.next({
              success: false,
              client: null
            })
          }
        }
      ))

    return toReturn.asObservable();
  }

  /**
   * Wyświetla dialog powitalny onboardingu (tour po aplikacji)
   */
  public showWelcomeOnboardingDialog(): void {
    this.dialog.open(WelcomeOnboardingDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      height: 'auto',
      maxHeight: '95vh',
      disableClose: true,
      panelClass: ['fakturnia-responsive-dialog', 'welcome-onboarding-dialog']
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll();
  }

  private _unsubscribeAll() {
    // Print subs to delete
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._subscriptions = [];
  }
}